import React, { useState } from "react";
import { Dialog, Button, Flex, Text, Callout } from "@radix-ui/themes";

import { MailCheck, MailX } from "lucide-react";
import RadixModalHeader from "./RadixModalHeader";
import { ConfirmIcon, CopyIcon, ICON_SIZE, SuccessIcon, WarningIcon } from "./actionIcons";
/**
 * What happened to a new user's invitation, shown to the admin who triggered it.
 *
 * WHY THIS IS A DIALOG AND NOT A TOAST. The server can only report that Brevo
 * ACCEPTED the email — it returns 201 for blocklisted recipients too, so a
 * successful send is not evidence of delivery. The admin is therefore the
 * fallback delivery mechanism, and `inviteUrl` exists so they can be one. A
 * toast that disappears after five seconds is no use for a link somebody has to
 * copy into WhatsApp.
 *
 * The predecessor of this screen was silence: /adduseraccount returned an
 * smsWarning that no caller in the app ever read, so a failed send produced a
 * cheerful "User account created" and a colleague who could not sign in.
 *
 * `inviteUrl` may be null — the server withholds it for accounts that have
 * already been signed into, so that resending an invite cannot be used to take
 * over an established user's account. The dialog stays useful without it.
 */
export default function InviteResultDialog({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  const { email, inviteUrl, emailWarning, smsWarning } = result;
  const delivered = !emailWarning;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      //Clipboard access is denied outside a secure context and in some mobile
      //browsers. The link is on screen and selectable, so this is recoverable
      //without a scary error — just don't claim it was copied.
      setCopied(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Content className="max-w-md" style={{ padding: 0, overflow: 'hidden' }}>
        <RadixModalHeader icon={delivered ? MailCheck : MailX} title={delivered ? "Invitation sent" : "Invitation not sent"} variant={delivered ? "accent" : "danger"} />
        <div className="modal-body-padded">
          <Flex direction="column" gap="3">
            {delivered ? (
              <Callout.Root color="green" size="1">
                <Callout.Icon><SuccessIcon size={ICON_SIZE} /></Callout.Icon>
                <Callout.Text>
                  We emailed <strong>{email}</strong> a link to set their password. It expires in 48 hours.
                </Callout.Text>
              </Callout.Root>
            ) : (
              <Callout.Root color="red" size="1">
                <Callout.Icon><WarningIcon size={ICON_SIZE} /></Callout.Icon>
                <Callout.Text>
                  The email to <strong>{email}</strong> could not be sent ({emailWarning}).
                  {inviteUrl && " Send them the link below yourself — they cannot sign in until they use it."}
                </Callout.Text>
              </Callout.Root>
            )}

            {smsWarning && (
              <Text size="1" color="gray">
                The heads-up SMS also failed ({smsWarning}). That one only tells them to check their email.
              </Text>
            )}

            {inviteUrl && (
              <div>
                <Text as="p" size="2" weight="medium" mb="1">Invitation link</Text>
                {/* Selectable even when the clipboard is unavailable, and wrapped
                    rather than truncated so nobody copies half a token. */}
                <Text
                  as="p"
                  size="1"
                  style={{
                    wordBreak: 'break-all', fontFamily: 'monospace',
                    background: 'var(--gray-3)', padding: '8px', borderRadius: '4px',
                  }}
                >
                  {inviteUrl}
                </Text>
                <Flex mt="2" gap="2" align="center">
                  <Button type="button" variant="soft" onClick={copyLink}>
                    <CopyIcon size={ICON_SIZE} /> {copied ? "Copied" : "Copy link"}
                  </Button>
                  <Text size="1" color="gray">Anyone with this link can set the password — share it with them only.</Text>
                </Flex>
              </div>
            )}
          </Flex>

          <Flex mt="4" justify="end">
            <Button type="button" onClick={onClose}><ConfirmIcon size={ICON_SIZE} />Done</Button>
          </Flex>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
